import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Upload,
    FileText,
    Trash2,
    Download,
    CheckCircle2,
    XCircle,
    Clock,
    ShieldCheck,
    Loader2,
} from "lucide-react";

const STATUS_META = {
    pending: { label: "En attente de vérification", cls: "pill pill-gold", icon: Clock },
    verified: { label: "Vérifié", cls: "pill pill-green", icon: ShieldCheck },
    rejected: { label: "Refusé", cls: "pill pill-red", icon: XCircle },
};

// Grouped display order (base + KBIS mandatory, then pro/security, then autre)
const CORE_TYPES = ["id_card", "residence_proof", "rib", "kbis"];
const AE_TYPES = ["insee", "attestation_vigilance"];
const PRO_TYPES = ["health_card", "diploma", "cv", "other"];

function DocRow({ type, label, docs, onUpload, onDelete, onDownload, uploading }) {
    const inputRef = useRef();
    const items = docs.filter((d) => d.doc_type === type);

    const trigger = () => inputRef.current?.click();
    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        onUpload(type, file);
        e.target.value = "";
    };

    return (
        <div
            className="border border-slate-200 rounded-xl p-4 bg-white"
            data-testid={`doc-row-${type}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-semibold text-sm">{label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        PDF, JPG ou PNG · 5 Mo max
                    </div>
                </div>
                <button
                    onClick={trigger}
                    disabled={uploading === type}
                    className="btn-ghost-navy text-xs disabled:opacity-50"
                    data-testid={`upload-btn-${type}`}
                >
                    {uploading === type ? (
                        <Loader2 size={12} className="inline animate-spin -mt-0.5 mr-1" />
                    ) : (
                        <Upload size={12} className="inline -mt-0.5 mr-1" />
                    )}
                    {items.length > 0 ? "Remplacer" : "Téléverser"}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    onChange={onFile}
                    className="hidden"
                    data-testid={`file-input-${type}`}
                />
            </div>
            {items.length > 0 && (
                <div className="mt-3 space-y-2">
                    {items.map((d) => {
                        const meta = STATUS_META[d.status] || STATUS_META.pending;
                        const Icon = meta.icon;
                        return (
                            <div
                                key={d.id}
                                className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-3 py-2"
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <FileText size={16} className="text-[#0B192C] shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium truncate">
                                            {d.original_filename}
                                        </div>
                                        <div className="text-[10px] text-slate-500">
                                            {(d.size / 1024).toFixed(0)} Ko
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <span className={meta.cls}>
                                        <Icon size={11} />
                                        {meta.label}
                                    </span>
                                    <button
                                        onClick={() => onDownload(d)}
                                        className="text-slate-500 hover:text-[#0B192C] p-1"
                                        title="Télécharger"
                                        data-testid={`download-doc-${d.id}`}
                                    >
                                        <Download size={14} />
                                    </button>
                                    <button
                                        onClick={() => onDelete(d)}
                                        className="text-slate-400 hover:text-red-600 p-1"
                                        title="Supprimer"
                                        data-testid={`delete-doc-${d.id}`}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function CandidateDocuments({ profileStatus }) {
    const [types, setTypes] = useState([]);
    const [docs, setDocs] = useState([]);
    const [uploading, setUploading] = useState(null);
    const [showAE, setShowAE] = useState(false);

    const refresh = async () => {
        try {
            const [t, d] = await Promise.all([
                api.get("/candidates/documents/types"),
                api.get("/candidates/documents"),
            ]);
            setTypes(t.data);
            setDocs(d.data);
        } catch (_) {}
    };

    useEffect(() => {
        refresh();
    }, []);

    const labelOf = (k) => types.find((t) => t.key === k)?.label || k;

    const uploadDoc = async (type, file) => {
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Fichier trop volumineux (max 5 Mo)");
            return;
        }
        setUploading(type);
        try {
            const fd = new FormData();
            fd.append("doc_type", type);
            fd.append("file", file);
            await api.post("/candidates/documents", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Document téléversé");
            await refresh();
        } catch (err) {
            toast.error(
                err?.response?.data?.detail || "Erreur lors du téléversement"
            );
        } finally {
            setUploading(null);
        }
    };

    const deleteDoc = async (d) => {
        if (!window.confirm(`Supprimer « ${d.original_filename} » ?`)) return;
        try {
            await api.delete(`/candidates/documents/${d.id}`);
            toast.success("Document supprimé");
            await refresh();
        } catch (_) {
            toast.error("Erreur");
        }
    };

    const downloadDoc = async (d) => {
        try {
            const resp = await api.get(
                `/candidates/documents/${d.id}/download`,
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = d.original_filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (_) {
            toast.error("Erreur de téléchargement");
        }
    };

    const totalUploaded = docs.length;
    const totalVerified = docs.filter((d) => d.status === "verified").length;

    return (
        <div className="sd-card p-6" data-testid="documents-section">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div>
                    <div className="overline mb-1">Documents administratifs</div>
                    <h2 className="font-display text-xl">Mes justificatifs</h2>
                    <p className="text-xs text-slate-500 mt-1 max-w-lg">
                        Téléversez vos documents pour valider votre profil. Ils
                        sont stockés de manière sécurisée et ne sont visibles
                        que par l'administrateur.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-2xl font-display font-black text-[#0B192C]">
                            {totalVerified}/{totalUploaded}
                        </div>
                        <div className="text-[10px] text-slate-500">Vérifiés</div>
                    </div>
                </div>
            </div>

            <div className="overline text-slate-500 mb-3">Pièces obligatoires (auto-entrepreneur)</div>
            <p className="text-xs text-slate-500 mb-3 -mt-2 max-w-lg">
                Ces 4 documents sont indispensables pour valider votre profil et
                démarrer les missions : carte d'identité, justificatif de
                domicile, RIB et KBIS (extrait auto-entrepreneur / société).
            </p>
            <div className="grid md:grid-cols-2 gap-3">
                {CORE_TYPES.map((t) => (
                    <DocRow
                        key={t}
                        type={t}
                        label={labelOf(t)}
                        docs={docs}
                        onUpload={uploadDoc}
                        onDelete={deleteDoc}
                        onDownload={downloadDoc}
                        uploading={uploading}
                    />
                ))}
            </div>


            <div className="mt-6">
                <button
                    onClick={() => setShowAE((v) => !v)}
                    className="overline hover:text-[#F5A623] transition-colors text-slate-500"
                    data-testid="toggle-ae-docs"
                >
                    {showAE ? "▾" : "▸"} Compléments auto-entrepreneur (INSEE, attestation de vigilance)
                </button>
                {showAE && (
                    <div className="grid md:grid-cols-2 gap-3 mt-3">
                        {AE_TYPES.map((t) => (
                            <DocRow
                                key={t}
                                type={t}
                                label={labelOf(t)}
                                docs={docs}
                                onUpload={uploadDoc}
                                onDelete={deleteDoc}
                                onDownload={downloadDoc}
                                uploading={uploading}
                            />
                        ))}
                    </div>
                )}
            </div>

            <div className="mt-6">
                <div className="overline text-slate-500 mb-3">
                    Documents professionnels (facultatif)
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    {PRO_TYPES.map((t) => (
                        <DocRow
                            key={t}
                            type={t}
                            label={labelOf(t)}
                            docs={docs}
                            onUpload={uploadDoc}
                            onDelete={deleteDoc}
                            onDownload={downloadDoc}
                            uploading={uploading}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
